import * as tslib_1 from "tslib";
import { HelperService } from './helper.service';
import { of } from 'rxjs';
import { SigninService } from './signin/signin.service';
import { permissionsForFeature, permissionSlugs } from './utils/permission.utils';
import * as i0 from "@angular/core";
import * as i1 from "./signin/signin.service";
export { permissionsForFeature, permissionSlugs };
var AuthService = /** @class */ (function () {
    function AuthService(signInService) {
        this.signInService = signInService;
        this.user = null;
        this._token = null;
        this.redirectUrl = null;
        this.permissions = null;
        this.authorizationPermissions = [];
        this.authzPanels = [];
        this.authorizationFetchFailed = false;
    }
    AuthService.prototype.setToken = function (token) {
        if (!token) {
            this._token = null;
            this.user = null;
            this.permissions = null;
            this.authorizationPermissions = [];
            return;
        }
        this._token = token;
    };
    Object.defineProperty(AuthService.prototype, "isUserLoggedIn", {
        get: function () {
            return this.token ? true : false;
        },
        enumerable: true,
        configurable: true
    });
    AuthService.prototype.fetchUserInfo = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var userDetails, error_1, status_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.signInService.getUserInfo().toPromise()];
                    case 1:
                        userDetails = _a.sent();
                        this.setUserDetails(userDetails);
                        return [2 /*return*/, userDetails];
                    case 2:
                        error_1 = _a.sent();
                        status_1 = error_1 && error_1.status;
                        if (status_1 == 401) {
                            this.clearSession();
                        }
                        return [2 /*return*/, null];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(AuthService.prototype, "token", {
        get: function () {
            return this._token;
        },
        enumerable: true,
        configurable: true
    });
    /** Persist session on shared-parent-domain cookies (CSCC + payroll Google login). */
    AuthService.prototype.establishSession = function (token, permission) {
        var normalized = HelperService.normalizeAuthToken(token);
        HelperService.setAuthCookies({ token: normalized, permission: permission });
        this.setToken(normalized || null);
    };
    AuthService.prototype.getPermissionCookie = function () {
        return HelperService.accessCookie('permission') || '';
    };
    AuthService.prototype.clearSession = function () {
        this.setToken(null);
        HelperService.clearAuthCookies();
    };
    AuthService.prototype.isAuthenticated = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var token;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        token = this.getToken();
                        this.setToken(token || null);
                        if (!token) {
                            return [2 /*return*/, false];
                        }
                        return [4 /*yield*/, this.fetchUserInfo()];
                    case 1:
                        _a.sent();
                        // 401 clears the session; treat as unauthenticated so SSO cookies cannot stick.
                        return [2 /*return*/, !!(this.getToken() && this._token)];
                }
            });
        });
    };
    AuthService.prototype.clearUserCred = function () {
        this.clearSession();
    };
    AuthService.prototype.getUserDetails = function () {
        return of(this.user);
    };
    AuthService.prototype.setUserDetails = function (user) {
        this.permissions = user.permissions;
        this.authorizationPermissions = user.authorizationPermissions || [];
        this.authzPanels = user.authzPanels || [];
        this.authorizationFetchFailed = user.authorizationFetchFailed || false;
        this.user = user;
    };
    AuthService.prototype.getToken = function () {
        return HelperService.normalizeAuthToken(HelperService.accessCookie('token'));
    };
    AuthService.prototype.clearToken = function () {
        this.clearSession();
    };
    AuthService.prototype.getPermissions = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this.permissions) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.fetchUserInfo()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/, this.permissions];
                }
            });
        });
    };
    AuthService.prototype.getAuthzPanels = function () {
        return this.authzPanels || [];
    };
    AuthService.prototype.getEffectivePermissions = function (feature) {
        var legacySlugs = this.normalizePermissionSlugs(this.permissions || []);
        var effectivePermissions = this.resolveFeatureAuthorization(feature, {
            authzPanels: this.authzPanels,
            legacyPermissions: legacySlugs,
            authorizationPermissions: this.authorizationPermissions,
        }).effectivePermissions;
        return effectivePermissions;
    };
    AuthService.prototype.normalizePermissionSlugs = function (permissions) {
        return (permissions || []).map(function (p) {
            if (typeof p === 'string') {
                return p;
            }
            return (p && (p.name || p.slug)) || '';
        }).filter(Boolean);
    };
    AuthService.prototype.isAuthorizedFor = function (permission, feature) {
        var required = Array.isArray(permission) ? permission.filter(Boolean) : (permission ? [permission] : []);
        if (!required.length) {
            return true;
        }
        var effectivePermissions = this.getEffectivePermissions(feature);
        if (!effectivePermissions || !effectivePermissions.length) {
            return false;
        }
        var userPermissionSlugs = this.normalizePermissionSlugs(effectivePermissions);
        return required.some(function (slug) { return userPermissionSlugs.includes(slug); });
    };
    AuthService.prototype.resolveFeatureAuthorization = function (feature, options) {
        if (options === void 0) { options = {}; }
        var authzPanels = options.authzPanels || [];
        var legacyPermissions = options.legacyPermissions || [];
        var authorizationPermissions = options.authorizationPermissions || [];
        var isAuthorizationPanel = Boolean(feature && authzPanels.includes(feature));
        if (!isAuthorizationPanel) {
            return {
                effectivePermissions: legacyPermissions,
                isAuthorizationPanel: false,
            };
        }
        // If resource requires AUTHZ flow but authorization fetch failed, throw error instead of falling back
        if (this.authorizationFetchFailed) {
            throw new Error("Authorization service fetch failed for feature: " + feature + ". Cannot determine access.");
        }
        return {
            effectivePermissions: authorizationPermissions,
            isAuthorizationPanel: true,
        };
    };
    AuthService.ngInjectableDef = i0.defineInjectable({ factory: function AuthService_Factory() { return new AuthService(i0.inject(i1.SigninService)); }, token: AuthService, providedIn: "root" });
    return AuthService;
}());
export { AuthService };
